<template>
  <div>
    <loading :active.sync="loading"
             :can-cancel="true"
             :is-full-page="true">
    </loading>
    <GmapMap class="map"
             :zoom="zoomLevel"
             :center="center"
             :options="mapOption"
             @zoom_changed="updateZoom($event)">
      <GmapCircle v-for="(marker, index) in markers"
                  :key="'Case' + index"
                  :center="marker.position"
                  :radius="getCaseRadius(marker.code)"
                  :visible="true"
                  @click="showCaseData(marker, index)"
                  :options="{fillColor:'yellow', fillOpacity:0.5, strokeOpacity: 0, zIndex: 0}"
      ></GmapCircle>
      <GmapCircle v-for="(marker, index) in markers"
                  :key="'Death' + index"
                  :center="marker.position"
                  :radius="getDeathRadius(marker.code)"
                  :visible="true"
                  @click="showCaseData(marker, index)"
                  :options="{fillColor:'red', fillOpacity:0.7, strokeOpacity: 0, zIndex: 1}"
      ></GmapCircle>
      <GmapInfoWindow :options="infoOptions"
                      :position="infoWindowPos"
                      :opened="infoWinOpen"
                      @closeclick="infoWinOpen=false">
      </GmapInfoWindow>
    </GmapMap>
    <h3 class="top-20"
        v-if="!load">
      {{ $t('map.title') }}
    </h3>
    <table class="table table-bordered top-20"
           v-if="!load">
      <thead>
        <tr>
          <th>{{ $t('map.region') }}</th>
          <th>{{ $t('map.cases') }}</th>
          <th>{{ $t('map.deaths') }}</th>
          <th>{{ $t('map.R0') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in sortedCaseData"
            :key="item.cc">
          <td>{{ countryDict[item.code] }}</td>
          <td>{{ item.cases }}</td>
          <td>{{ item.deaths }}</td>
          <td v-bind:class="getR0Class(item.r0)">{{ getR0Text(item.r0) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapGetters } from 'vuex'

export default {
    data() {
        return {
            center: { lat: 0, lng: 0 },
            caseData: { },
            sortedCaseData: [ ],
            markers: [ ],
            place: null,
            load: false,
            mapOption: {
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: true,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: true,
                disableDefaultUi: false
            },
            infoOptions: {
                content: ''
            },
            infoWindowPos: null,
            infoWinOpen: false,
            currentMarkerIdx: null,
            maxCaseNum: 1,
            zoomLevel: window.width < 800 ? 1 : 2,
            locationMap: {}
        }
    },
    created() {
        this.updateCountries()
        this.fetchData()
    },
    description: 'COVID-19 Map',
    methods: {
        setDescription(description) {
            this.description = description
        },
        updateCountries() {
            this.locationMap = this.countryData.reduce(function(map, obj) {
                map[obj.alpha3] = {lat: obj.latitude, lng: obj.longitude}
                return map
            }, {})
        },
        getCaseRadius(country) {
            let cases = this.caseData[country].cases
            let latitude = this.locationMap[country].lat
            let radiusRate = this.radiusCaseRate * Math.cos(latitude / 180 * Math.PI)
            if (cases === 0) {
                return 0
            } else if (cases <= 1000) {
                return Math.sqrt(1000) * radiusRate
            }
            return Math.sqrt(cases) * radiusRate
        },
        getDeathRadius(country) {
            let deaths = this.caseData[country].deaths
            let latitude = this.locationMap[country].lat
            let radiusRate = this.radiusCaseRate * Math.cos(latitude / 180 * Math.PI)
            if (deaths === 0) {
                return 0
            } else if (deaths <= 200) {
                return Math.sqrt(200) * radiusRate
            }
            return Math.sqrt(deaths) * radiusRate
        },
        fetchData() {
            this.loading = true
            const url = 'https://api.petal.network/ecdc'
            this.$http.get(url).then(data => {
                this.loading = false
                if (data.status >= 400) {
                    alert('Failed to load data!')
                    return
                }
                let res = JSON.parse(data.bodyText)
                if (!res || !res.hasOwnProperty('records')) {
                    alert('Invalid data!')
                    return
                }
                this.updateCaseData(res.records)
            }).catch(e => {
                console.log(e)
                this.loading = false
            })
        },
        updateCaseData(records) {
            let nowTs = new Date().getTime()
            let oneDayMS = 1000 * 3600 * 24
            records.forEach(rec => {
                let cc = rec.countryterritoryCode
                if (cc === 'N/A' || cc === '') return
                if (!this.caseData.hasOwnProperty(cc)) {
                    if (!this.countryDict.hasOwnProperty(cc)) {
                        console.log('Country code not found: ' + cc)
                        return
                    }
                    this.caseData[cc] = {
                        code: cc,
                        cases: 0,
                        deaths: 0,
                        newCases: 0,
                        newDeaths: 0,
                        preOldCases: [],
                        preNewCases: [],
                        r0: 0,
                        deathRate: '0%'
                    }
                }
                this.caseData[cc].cases += parseInt(rec.cases)
                this.caseData[cc].deaths += parseInt(rec.deaths)
                if (this.caseData[cc].cases > this.maxCaseNum) {
                    this.maxCaseNum = this.caseData[cc].cases
                }
                let recDate = new Date(rec.year, parseInt(rec.month) - 1, rec.day)
                let daysDiff = (nowTs - recDate.getTime()) / oneDayMS
                if (daysDiff < 2 && this.caseData[cc].newCases === 0 && this.caseData[cc].newDeaths === 0) {
                    this.caseData[cc].newCases = parseInt(rec.cases)
                    this.caseData[cc].newDeaths = parseInt(rec.deaths)
                }
                if (daysDiff <= 3) {
                    this.caseData[cc].preNewCases.push(parseInt(rec.cases))
                } else if (daysDiff <= 10) {
                    this.caseData[cc].preOldCases.push(parseInt(rec.cases))
                }
            })
            Object.keys(this.caseData).forEach(cc => {
                let item = this.caseData[cc]
                this.caseData[cc].r0 = this.calculateR0(item.preNewCases, item.preOldCases)
                this.caseData[cc].deathRate = this.deathRate(item.deaths, item.cases).toFixed(1) + '%'
            })
            this.sortedCaseData = Object.values(this.caseData)
            this.sortedCaseData.sort((a, b) => (a.cases > b.cases) ? -1 : 1)
            this.updateMarker()
        },
        updateMarker() {
            this.markers = [ ]
            Object.keys(this.caseData).forEach(cc => {
                if (this.locationMap.hasOwnProperty(cc)) {
                    let item = this.caseData[cc]
                    let marker = {
                        name: this.countryDict[cc],
                        code: cc,
                        position: this.locationMap[cc],
                        infoText: '<b>' + this.countryDict[cc] + '</b><br/>' +
                            '<b>' + this.$t('map.cases') + ':</b>' + item.cases + '<br/>' +
                            '<b>' + this.$t('map.deaths') + ':</b>' + item.deaths + '<br/>' +
                            '<b>' + this.$t('map.newCases') + ':</b>' + item.newCases + '<br/>' +
                            '<b>' + this.$t('map.newDeaths') + ':</b>' + item.newDeaths + '<br/>' +
                            '<b>' + this.$t('map.deathRate') + ':</b>' + item.deathRate + '<br/>' +
                            '<b>' + this.$t('map.R0') + ':</b><span ' + this.getR0Style(item.r0) + '>' + this.getR0Text(item.r0) + '</span><br/>'
                    }
                    this.markers.push(marker)
                }
            })
        },
        getR0Text(r0) {
            if (r0 <= 0.5) {
                return '↓ (R0≈' + r0.toFixed(1) + ')'
            } else if (r0 <= 1) {
                return '- (R0≈' + r0.toFixed(1) + ')'
            } else {
                return '↑ (R0≈' + r0.toFixed(1) + ')'
            }
        },
        getR0Class(r0) {
            if (r0 <= 0.5) {
                return 'safe'
            } else if (r0 <= 1) {
                return 'normal'
            } else {
                return 'danger'
            }
        },
        getR0Style(r0) {
            if (r0 <= 0.5) {
                return 'style="color: #00b887"'
            } else if (r0 <= 1) {
                return 'style="color: #cea608"'
            } else {
                return 'style="color: #ff0303"'
            }
        },
        calculateR0(preNewCases, preOldCases) {
            let newCaseAvg = this.calculateAvg(preNewCases)
            let oldCaseAvg = this.calculateAvg(preOldCases)
            if (oldCaseAvg < 1) {
                oldCaseAvg = 1
            }
            if (newCaseAvg === 0) {
                return 0
            } else {
                let r0 = newCaseAvg / oldCaseAvg
                return Math.min(r0, 9)
            }
        },
        calculateAvg(numArray) {
            if (numArray.length === 0) return 0
            let sum = 0
            for (let i = 0; i < numArray.length; i++) {
                sum += numArray[i]
            }
            return sum / numArray.length
        },
        showCaseData(marker, idx) {
            this.infoWindowPos = marker.position
            this.infoOptions.content = marker.infoText

            if (this.currentMarkerIdx === idx) {
                this.infoWinOpen = !this.infoWinOpen
            } else {
                this.infoWinOpen = true
                this.currentMarkerIdx = idx
            }
        },
        updateZoom(level) {
            this.zoomLevel = level
        },
        deathRate(deaths, cases) {
            return deaths * 100 / cases
        }
    },
    computed: {
        ...mapGetters(['countryData', 'countryDict']),
        maxRadius() {
            return 1500000 / this.zoomLevel
        },
        radiusCaseRate() {
            return this.maxRadius / Math.sqrt(100000)
        }
    },
    components: {
        Loading
    }
}
</script>
<style scoped>
.map {
    width: 100%;
    height: 400px;
}
.top-20 {
    margin-top: 20px;
}
.danger {
    color: #ff0303
}
.normal {
    color: #cea608
}
.safe {
    color: #00b887
}
.text-center {
    width: 100%;
    text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/CovidMap.vue